// The ONE module that names the editor-agent endpoint (ENG-1029) — the same
// ownership rule assetManagerApi.js holds for AAM routes. The client always
// calls the relative /agent base: the Vite dev proxy (agentProxyTarget.js) and
// nginx's `location /agent/` (nginx.conf.template) each own their hop.

import { createSseParser, parseAgentEvents } from './sseParse.js'

const CHAT_PATH = '/agent/chat'

/**
 * Stream one agent turn. POSTs the chat history + editor context and invokes
 * `onEvent(event)` for every SSE event the server emits
 * ({type: 'text-delta'|'tool-start'|'tool-result'|'warning'|'done'|'error'}).
 *
 * Auth mirrors apiClient.js: cookies ride along (deployed), and a 401 gets ONE
 * /auth/refresh + retry before failing loudly. 404 is reported as the endpoint
 * being unavailable — the deployment hasn't enabled the agent — so the panel
 * can show that state honestly instead of a generic error.
 *
 * Resolves when the stream closes; rejects on transport/HTTP failure. Server-side
 * agent errors arrive as {type:'error'} events (the stream itself is a 200).
 */
export async function streamAgentChat({ messages, context, signal, onEvent }) {
  const res = await fetchWithRefresh(CHAT_PATH, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ messages, context }),
    signal,
  })

  if (res.status === 404) {
    const err = new Error('Agent endpoint not available on this deployment (AOS_AGENT_LLM_KEY unset on the MCP server).')
    err.unavailable = true
    throw err
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`agent chat failed: ${res.status} ${text.slice(0, 300) || res.statusText}`)
  }

  const parser = createSseParser()
  const decoder = new TextDecoder()
  const reader = res.body.getReader()
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    for (const ev of parseAgentEvents(parser.push(decoder.decode(value, { stream: true })))) onEvent(ev)
  }
  for (const ev of parseAgentEvents(parser.end())) onEvent(ev)
}

// One 401 → POST /auth/refresh → retry, mirroring apiClient.js. Raw fetch here
// because apiFetch consumes the body — a stream needs the Response itself.
async function fetchWithRefresh(path, init) {
  let res = await fetch(path, init)
  if (res.status === 401) {
    const refresh = await fetch('/auth/refresh', { method: 'POST', credentials: 'include' })
    if (refresh.ok) res = await fetch(path, init)
  }
  return res
}
